import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

import DepartmentMembersTable from './DepartmentMembersTable';
import ConfirmInviteUserDialog from 'components/Shared/ConfirmInviteUserDialog';
import AddTeamMemberDialog from 'components/Employees/AddTeamMemberDialog';

import { setPersonalInfoInitialState } from 'actions/employeeActions';
import { fetchDepartmentMembers, registerDepartmentEmployee, exportDepartment } from 'actions/departmentActions';
import { getAgencyId, getUserId, getAccess } from 'reducers';

import { ROLES } from 'constants/userConstants';
import {
    SORT_QUERY_SELECTOR,
    SEARCH_QUERY_SELECTOR,
} from './DepartmentMembersTable/DepartmentMembersTableConstants';
import { DATE_FORMAT_WITH_DASH } from 'constants/commonConstants';

import { parseQueryString } from 'utils/helpers';

import './DepartmentMembers.scss'

const DepartmentMembers = ({
    agencyId,
    userId,
    accessAsManager,
    departmentId,
    fetchDepartmentMembers,
    registerDepartmentEmployee,
    exportDepartment,
}) => {
    const history = useHistory();

    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [newUserData, setNewUserData] = useState({});
    const [reload, setReload] = useState(false);

    const handleOpenAddDialog = () => {
        setIsAddDialogOpen(true);
    };

    const handleCloseAddDialog = () => {
        setNewUserData({});
        setIsAddDialogOpen(false);
    };

    const handleCloseConfirmDialog = () => setIsConfirmDialogOpen(false);

    const handleRegisterEmployee = async employeeData => {
        const { email, name, employmentType, startDate } = employeeData;

        employeeData = {
            email,
            name,
            employmentInformation: {
                generalInfo: { profileVisibility: 'Private', startDate },
                contractInformation: { employmentType }
            },
            role: ROLES.EMPLOYEE,
            addedBy: userId,
        };

        if (employeeData.email) {
            setNewUserData(employeeData);
            setIsConfirmDialogOpen(true);
            return;
        }

        await registerDepartmentEmployee(agencyId, departmentId, employeeData);
        setReload(true);
        handleCloseAddDialog();
    };

    const handleConfirmInviteUserClick = async () => {
        try {
            handleCloseConfirmDialog();
            await registerDepartmentEmployee(agencyId, departmentId, newUserData);
            setReload(true);
            handleCloseAddDialog();
        } catch (error) {
            setNewUserData(prevState => ({ ...prevState, email: '' }));
            handleCloseConfirmDialog();
        }
    };

    const handleExportDepartment = () => {
        const parsedQuery = parseQueryString(history.location.search);
        const sortBy = parsedQuery[SORT_QUERY_SELECTOR];
        const sortByDir = parsedQuery[`${SORT_QUERY_SELECTOR}Dir`];
        const search = parsedQuery[SEARCH_QUERY_SELECTOR];
        const { name: departmentName = '' } = accessAsManager.find(x => x._id === departmentId) || {};
        const fileName = `${moment().format(DATE_FORMAT_WITH_DASH)}-${departmentName}`;

        exportDepartment(agencyId, departmentId, { sortBy, sortByDir, search }, fileName);
    }

    return (
        <div className="department-members-wrapper">
            <DepartmentMembersTable
                key={departmentId} // User can have multiple departments, so this re-renders the component on department change.  
                handleOpenAddDialog={handleOpenAddDialog}
                handleExportDepartment={handleExportDepartment}
                reload={reload}
                setReload={setReload}
                tableFetchAction={fetchDepartmentMembers.bind(null, agencyId, departmentId)}
            />

            <AddTeamMemberDialog
                isOpen={isAddDialogOpen}
                isAddingEmployee={true}
                newUserData={newUserData}
                handleCloseDialog={handleCloseAddDialog}
                handleRegisterMember={handleRegisterEmployee}
            />

            <ConfirmInviteUserDialog
                openDialog={isConfirmDialogOpen}
                invitedUser={ROLES.EMPLOYEE}
                handleCloseDialog={handleCloseConfirmDialog}
                handleInviteUser={handleConfirmInviteUserClick}
            />
        </div>
    );
};

const mapStateToProps = state => ({
    agencyId: getAgencyId(state),
    userId: getUserId(state),
    accessAsManager: getAccess(state),
});

const mapDispatchToProps = {
    fetchDepartmentMembers,
    setPersonalInfoInitialState,
    registerDepartmentEmployee,
    exportDepartment,
};

export default connect(mapStateToProps, mapDispatchToProps)(DepartmentMembers);
